import React, { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCloseOnEscape } from '../../hooks/useCloseOnEscape';
import { LoginModal, SignupModal, VenueLoginModal, VenueSignupModal } from '../Auth';
import HeaderSearchBar from './HeaderSearchBar';
import HeaderLogo from './HeaderLogo';
import DesktopNavigation from './DesktopNavigation';
import MobileMenuButton from './MobileMenuButton';
import AskHelpButton from '../Comms/AskHelpButton';
import MobileMenu from './MobileMenu';
import LogoutConfirmModal from './LogoutConfirmModal';
import { useModalState } from './hooks/useModalState';
import { useClickOutside } from './hooks/useClickOutside';
import './Header.css';

const Header = ({ 
  showSearchBar = false, 
  searchData = null, 
  setSearchData = null, 
  handleSearchSubmit = null,
  handleLocationSelect = null,
  showLocationDropdown = false,
  setShowLocationDropdown = null,
  searchOptions = [],
  defaultSearchOptions = [],
  landingPage = false
}) => {
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const mobileMenuRef = useRef(null);
  
  const { currentUser, logout, isLoggingOut } = useAuth();
  const navigate = useNavigate();

  const {
    showLoginModal,
    showSignupModal,
    showVenueLoginModal,
    showVenueSignupModal,
    handleLoginClick,
    closeLoginModal,
    closeSignupModal,
    closeVenueLoginModal,
    closeVenueSignupModal,
    switchToVenueLogin,
    switchToVenueSignup,
    switchToSignup,
    switchToLogin
  } = useModalState();

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutModal(false);
    await logout();
    navigate('/');
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  useCloseOnEscape(isMobileMenuOpen, closeMobileMenu);

  // Close mobile menu when clicking outside
  useClickOutside(mobileMenuRef, () => {
    if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
  });

  return (
    <header
      className={isLandingPage ? 'landing-header' : 'standard-header'}
      style={{ padding: '0' }}
    >
      <div className="header-brand-section">
        <HeaderLogo isLandingPage={isLandingPage} />

        {showSearchBar && (
          <HeaderSearchBar
            searchData={searchData}
            setSearchData={setSearchData}
            handleSearchSubmit={handleSearchSubmit}
            handleLocationSelect={handleLocationSelect}
            showLocationDropdown={showLocationDropdown}
            setShowLocationDropdown={setShowLocationDropdown}
            defaultSearchOptions={defaultSearchOptions}
          />
        )}
      </div>

      <DesktopNavigation
        isLandingPage={isLandingPage}
        currentUser={currentUser}
        onLogout={handleLogoutClick}
        isLoggingOut={isLoggingOut}
        onLoginClick={handleLoginClick}
      />

      <AskHelpButton />
      <MobileMenuButton 
        isOpen={isMobileMenuOpen} 
        onClick={toggleMobileMenu}
        isLandingPage={isLandingPage}
      />

      <MobileMenu
        isOpen={isMobileMenuOpen}
        menuRef={mobileMenuRef}
        isLandingPage={isLandingPage}
        showSearchBar={showSearchBar}
        currentUser={currentUser}
        onLogout={handleLogoutClick}
        isLoggingOut={isLoggingOut}
        onLoginClick={handleLoginClick}
        onCloseMenu={closeMobileMenu}
      />

      {/* Authentication Modals */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={closeLoginModal}
        onSwitchToSignup={switchToSignup} 
      />
      <SignupModal 
        isOpen={showSignupModal} 
        onClose={closeSignupModal}
        onSwitchToLogin={switchToLogin} 
      />
      <VenueLoginModal 
        isOpen={showVenueLoginModal} 
        onClose={closeVenueLoginModal}
        onSwitchToSignup={switchToVenueSignup} 
      />
      <VenueSignupModal 
        isOpen={showVenueSignupModal} 
        onClose={closeVenueSignupModal}
        onSwitchToLogin={switchToVenueLogin} 
      />
      
      {/* Logout Confirmation Modal */}
      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onClose={handleCancelLogout}
        onConfirm={handleConfirmLogout}
        isLoggingOut={isLoggingOut}
      />
    </header>
  );
};

export default Header;
